import { debounce, get } from 'lodash';
import React, { useCallback, useEffect, useRef } from 'react';
import { FieldValues } from 'react-hook-form';
import Bluebird from 'bluebird';
import { useQuestionnaireContext, useQuestionnaireFormContext } from '@/components/Questionnaire/QuestionnaireProvider';
import useNoodleApi from '@/hooks/useNoodleApi';
import runAiAgent from '@/tsClient/questionnaires/runAiAgent';
import { AiAgent } from '@/typings/questionnaires-api-models';
import rerunAiAgentOnQuestionnaireField from '@/tsClient/questionnaires/rerunAiAgentOnQuestionnaireField';
import removeNullish from '@/helpers/removeNullish';
import simpleDebug from '@/helpers/simpleDebug';
import { Questionnaire } from '@/typings/Questionnaire';
import { useEventsContext } from '@/providers/Events';
import FieldKeyConstants from '../../helpers/fieldKeys';
import parseBoxIndexRules from './boxIndexHelpers/parseBoxIndexRules';
import processWithRules from './boxIndexHelpers/processWithRules';
import parseFieldKey, { ParsedField } from '../../helpers/parseFieldKey';
import useInputFieldKeys from './helpers/useInputFieldKeys';
import useOutputFieldKeys from './helpers/useOutputFieldKeys';
import useWatchFields from '../../helpers/useWatchFields';
import getCountyFromAddress from './helpers/getCountyFromAddress';
import shouldSetValue from './helpers/shouldSetValue';
import getBoxIndexOutputFields from './boxIndexHelpers/getBoxIndexOutputFields';
import { FieldInputData } from './helpers/typings';
import Chunks, { ChunkSize, pickChunkSize } from './helpers/Chunks';
import areValuesEqual from '../../helpers/areValuesEqual';
import pickRowsToRun from './boxIndexHelpers/pickRowsToRun';
import { determineType } from '../../FormField/renderPopulatedFrom';

type Props = {
  key: string;
  aiAgent: AiAgent & {
    // placeholder for making this settable
    chunkSize?: ChunkSize;
  };
  questionnaireId: string;
  filter?: Parameters<typeof useOutputFieldKeys>[0]['filter'];
  onStartAgent: React.Dispatch<React.SetStateAction<string[]>>;
};

type Chunk = {
  numItems: number;
  fieldInputData: FieldInputData;
  fieldOutputs: ParsedField[];
};

type RunAgentProps = {
  agent: AiAgent;
  chunks: Chunk[];
  formValues: FieldValues;
  reRunData?: {
    contextId: string;
    fieldId: string;
    fieldKey: string;
  };
};

const whereDoYouLiveFieldKey = 'whereYouLive';

const HandleAiAgentAutofill: React.FC<Props> = ({ aiAgent, filter, questionnaireId, onStartAgent }) => {
  const { fieldsByKey, userWorkflowId } = useQuestionnaireContext();
  const { formMethods, getResponseSource, setValue } = useQuestionnaireFormContext();

  const chunkSizeRef = useRef<ChunkSize>(1);
  chunkSizeRef.current = pickChunkSize(aiAgent);

  const outputFieldKeysMain = useOutputFieldKeys({ aiAgent, filter });
  const inputFieldKeysMain = useInputFieldKeys({ aiAgent, enabled: outputFieldKeysMain.length > 0 });

  const watchedFields = useWatchFields(inputFieldKeysMain);
  const previousRunValuesRef = useRef<Record<string, unknown> | null>(null);

  const { getData: rerunAiAgentFn } = useNoodleApi(rerunAiAgentOnQuestionnaireField);

  const { addListener, removeListener, emit } = useEventsContext();

  const { getData: runAiAgentFn } = useNoodleApi(runAiAgent);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- debounce...
  const debouncedRunAgent = useCallback<(args: RunAgentProps) => Promise<void> | void>(
    debounce(async ({ agent, chunks, formValues, reRunData }: RunAgentProps): Promise<void> => {
      const chunksToRun = chunks.filter(chunk => Object.keys(chunk.fieldInputData).length > 0);
      chunksToRun.forEach(chunk => {
        const outputFieldKeys = chunk.fieldOutputs.map(of => of.fullFieldKey);
        onStartAgent(prev => [...prev, ...outputFieldKeys]);
      });
      await Bluebird.map(
        chunks,
        async chunk => {
          const { fieldInputData, fieldOutputs } = chunk;
          const outputFieldKeys = chunk.fieldOutputs.map(of => of.fullFieldKey);
          if (agent.aiModel === 'omitForPdfFills') {
            fieldOutputs?.forEach(outputField => {
              const masterFieldKey = outputField.listFieldKey;
              const indexNumber = outputField.rowIndex;

              if (masterFieldKey && typeof indexNumber === 'number' && indexNumber >= 0) {
                let amountDecider = false;
                let statusDecider = false;
                agent?.fieldData?.forEach(f => {
                  const fetchedValue = formValues[masterFieldKey][indexNumber][f];

                  if (typeof fetchedValue === 'object' && fetchedValue !== null) {
                    // This is the "amount" field
                    amountDecider = fetchedValue?.amount === 'NaN' || Number(fetchedValue?.amount) <= 0;
                  } else {
                    // This is the "status" field
                    statusDecider = fetchedValue === 'closed';
                  }
                });

                const finalDecider = amountDecider && statusDecider;

                setValue({
                  hasAutofillRun: true,
                  key: `${masterFieldKey}[${indexNumber}].${FieldKeyConstants.MASTER_CREDITOR_LIST_OMIT_FROM_PDFS_FIELD_KEY}`,
                  value: finalDecider ? ['omit'] : [],
                });
              }
            });
          } else if (agent.aiModel === 'boxIndex') {
            const rules = parseBoxIndexRules(aiAgent.instructions);
            pickRowsToRun(fieldOutputs).forEach(outputField => {
              const masterFieldKey = outputField.listFieldKey;
              const indexNumber = outputField.rowIndex;
              if (indexNumber !== null && masterFieldKey !== null) {
                getBoxIndexOutputFields(outputField, formValues).forEach(fieldToUpdate => {
                  const value = processWithRules({
                    formValues,
                    indexNumber: fieldToUpdate.index,
                    masterFieldKey,
                    rules,
                  });
                  if (value !== undefined) {
                    setValue({ hasAutofillRun: true, key: fieldToUpdate.key, source: 'ai', value });
                  } else {
                    setValue({ hasAutofillRun: true, key: fieldToUpdate.key, source: 'ai', value: null });
                  }
                });
              }
            });
          } else if (agent.aiModel === 'googlePlaces') {
            await Bluebird.map(
              Object.keys(fieldInputData),
              async inputFieldKey => {
                const data = fieldInputData[inputFieldKey];
                const usAddress = data.value ? (data.value as unknown) : null;
                const county = await getCountyFromAddress(usAddress);

                if (!county) {
                  fieldOutputs.forEach(outputField => {
                    setValue({ hasAutofillRun: true, key: outputField.fullFieldKey, source: 'ai', value: null });
                  });
                  return;
                }

                const parsedInputField = parseFieldKey(inputFieldKey);

                fieldOutputs.forEach(outputField => {
                  if (shouldSetValue({ inputField: parsedInputField, outputField })) {
                    setValue({ hasAutofillRun: true, key: outputField.fullFieldKey, source: 'ai', value: county });
                  }
                });
              },
              { concurrency: 3 },
            );
          } else {
            let metaInput = {};
            if (aiAgent.slug === 'get-filing-district') {
              const fieldKey = aiAgent.fieldData?.[0];
              const zipcode = formValues[whereDoYouLiveFieldKey]?.[0]?.[fieldKey]?.zip;
              if (!zipcode) return;
              metaInput = {
                ...metaInput,
                [fieldKey]: {
                  label: 'Zipcode',
                  value: zipcode,
                },
              };
            }
            const stateOfFiling = formMethods.getValues()[FieldKeyConstants.STATE_OF_FILING] as string | null;
            const result = await runAiAgentFn({
              agentId: agent.id,
              creatorId: agent.creatorId,
              fieldInputData: {
                ...fieldInputData,
                ...metaInput,
              },
              questionnaireId,
              questionnaireTemplateId: agent.questionnaireTemplateId,
              rowIndiciesToRun: fieldOutputs.map(of => of.rowIndex).filter(removeNullish),
              stateOfFiling,
              userWorkflowId,
            });

            const aiAgentAnswers = result.data;

            if (aiAgentAnswers) {
              Object.entries(aiAgentAnswers).forEach(([key, value]) => {
                if ((getResponseSource(key) !== 'manual' || reRunData) && key !== 'aiRequestId') {
                  const { fieldKey: keyBuilt } = parseFieldKey(key);
                  setValue({ aiRequestId: aiAgentAnswers?.aiRequestId, hasAutofillRun: true, key, source: 'ai', value });
                  if (key === keyBuilt) {
                    const fieldsFetched = fieldsByKey[keyBuilt];
                    fieldsFetched?.linkedFieldKeys?.forEach(linkedFieldKey => {
                      setValue({ aiRequestId: aiAgentAnswers?.aiRequestId, hasAutofillRun: true, key: linkedFieldKey, source: 'ai', value });
                    });
                  }
                }
              });
            }
          }

          onStartAgent(prev => prev.filter(field => !outputFieldKeys.includes(field)));
        },
        { concurrency: 3 },
      );
      if (reRunData) {
        const { contextId, fieldId, fieldKey } = reRunData;
        if (chunksToRun.length === 0) {
          const previousValue = formValues[reRunData.fieldId] ?? null;
          setValue({ hasAutofillRun: true, key: fieldKey, source: 'ai', value: previousValue });
        }
        emit('autofillRerunning', { contextId, fieldId, status: false });
      }
    }, 3000),
  [],
  );

  const reRunAiAgent = async ({
    field,
    formValues,
    contextId,
  }: {
    formValues: FieldValues;
    field: Questionnaire.FieldCommon | Questionnaire.Field;
    contextId: string;
  }) : Promise<void> => {
    const sourceType = determineType(field.autofill) ?? 'unknown';
    const res = await rerunAiAgentFn({ externalDataSetIds: [], fieldKey: field.key, questionnaireId });
    const data = res?.data;
    if (!data) {
      const previousValue = formValues[field.key] ?? null;
      setValue({ hasAutofillRun: true, key: field.key, source: sourceType, value: previousValue });
      emit('autofillRerunning', { contextId, fieldId: field.id, status: false });
      return;
    }
    const value = get(data, field.key, null);
    setValue({ aiRequestId: data?.aiRequestId, hasAutofillRun: true, key: field.key, source: sourceType, value });
    const { fieldKey: keyBuilt } = parseFieldKey(field.key);
    if (field.key === keyBuilt) {
      const fieldFetched = fieldsByKey[keyBuilt];
      fieldFetched?.linkedFieldKeys?.forEach(linkedFieldKey => {
        setValue({ aiRequestId: data?.aiRequestId, hasAutofillRun: true, key: linkedFieldKey, source: sourceType, value });
      });
    }
    emit('autofillRerunning', { contextId, fieldId: field.id, status: false });
  };

  useEffect(() => {
    // @TODO: figure out how to debounce this.
    simpleDebug(aiAgent.slug, 'in useEffect', {
      fieldData: aiAgent.fieldData,
      inputFieldKeysMain,
      outputFieldKeysMain,
      previousRunValues: previousRunValuesRef.current,
      watchedFields,
    });
    if (!aiAgent.fieldData || !watchedFields || outputFieldKeysMain.length === 0) return;

    const previousRunValues = previousRunValuesRef.current;

    // Set the previous values so that we can see if they change next time.
    // Set to null to start so that we can detect first load.
    previousRunValuesRef.current = watchedFields;

    if (previousRunValues && areValuesEqual(previousRunValues, watchedFields)) {
      return;
    }

    const formValues = formMethods.getValues();

    const chunkHandler = new Chunks({
      aiAgent,
      fieldsByKey,
      formValues,
      getResponseSource,
      inputFieldKeys: inputFieldKeysMain,
      outputFieldKeys: outputFieldKeysMain,
      size: chunkSizeRef.current,
    });

    // First find any unset output values and see if we can run the agent on those
    chunkHandler.addOutputFields({ unset: true });

    // Then look at all input field values and check if any of them have changed
    // If so, and all of the inputs are set for that input, run the agent on that row.
    // But only if this is not on the initial mount.
    if (previousRunValues) {
      chunkHandler.handleWatchedFields({
        watchedFields,
        watchedFieldsPrevious: previousRunValues,
      });
    }

    simpleDebug(aiAgent.slug, `Final chunks`, {
      chunks: chunkHandler.chunks,
    });

    if (chunkHandler.chunks.length > 0) {
      previousRunValuesRef.current = watchedFields;
      debouncedRunAgent({
        agent: aiAgent,
        chunks: chunkHandler.chunks,
        formValues,
      });
    }
  }, [aiAgent, watchedFields, debouncedRunAgent, outputFieldKeysMain, inputFieldKeysMain, formMethods, getResponseSource, fieldsByKey]);

  useEffect(() => {
    const listener = addListener('reRunAutofill', async ({ field, contextId: heardContextId }) => {
      if (!field || !field.key || !aiAgent) return;
      const aiActions = ['ai-agent', 'get-filing-county', 'get-filing-district'];

      if (outputFieldKeysMain.includes(field.key) && aiActions.includes(field.autofill?.action || '')) {
        emit('autofillRerunning', { contextId: heardContextId, fieldId: field.id, status: true });
        const formValues = formMethods.getValues();

        if (aiAgent.aiModel === 'boxIndex' || aiAgent.aiModel === 'googlePlaces' || aiAgent.aiModel === 'omitForPdfFills') {
          const chunkHandler = new Chunks({
            aiAgent,
            allowManual: true,
            fieldsByKey,
            formValues: formMethods.getValues(),
            getResponseSource,
            inputFieldKeys: inputFieldKeysMain,
            outputFieldKeys: outputFieldKeysMain,
            size: chunkSizeRef.current,
          });
          chunkHandler.addOutputFields({ unset: false });
          debouncedRunAgent({
            agent: aiAgent,
            chunks: chunkHandler.chunks,
            formValues,
            reRunData: {
              contextId: heardContextId,
              fieldId: field.id,
              fieldKey: field.key,
            },
          });
        } else {
          reRunAiAgent({
            contextId: heardContextId,
            field,
            formValues,
          });
        }
      }
    });
    return () => removeListener('reRunAutofill', listener);
  }, [addListener, removeListener, emit]);

  return null;
};

export default HandleAiAgentAutofill;
